import { useState, useCallback, useRef } from 'react'
import type { AttentionLevel, RecurrenceRule, Task } from '@/src/renderer/lib/db/models'
import { recurrenceUpdate } from '@/src/renderer/lib/recurrence'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import { useSoftRemoveSound, useSoftAddSound } from '@/src/renderer/lib/sounds'
import type { DurationValue, TaskEntryMode, TaskEntryData, TaskCreationData } from '../TaskEntry.types'
import type { StartValue } from '../../WhenDropdown'
import type { DeadlineValue } from '../../DeadlineDropdown'

export interface TaskFormFields {
  title: string
  notes: string
  attention: AttentionLevel | null
  duration: DurationValue | null
  tags: string[]
  /** Catalogue reward id, or null. */
  reward: string | null
  start: StartValue | null
  deadline: DeadlineValue | null
  /** Checklist buffered while creating; empty once the task exists. */
  subtaskDrafts: { title: string; done: boolean }[]
  /** Die **Wiederholung**, oder null. */
  recurrence?: RecurrenceRule | null
}

/** Assemble the submit payload from the current form fields (title/notes trimmed). */
export function buildCreationData(fields: TaskFormFields): TaskCreationData {
  return {
    title: fields.title.trim(),
    description: fields.notes.trim() || null,
    attention: fields.attention,
    estimatedDuration: fields.duration?.minutes ?? null,
    durationMode: fields.duration?.mode ?? null,
    startDate: fields.start?.date ?? null,
    startTime: fields.start?.time ?? null,
    startPeriod: fields.start?.period ?? null,
    dueDate: fields.deadline?.date ?? null,
    dueTime: fields.deadline?.time ?? null,
    duePeriod: fields.deadline?.period ?? null,
    tags: fields.tags,
    rewardId: fields.reward,
    subtasks: fields.subtaskDrafts,
    recurrence: fields.recurrence,
  }
}

/**
 * Field-by-field comparison of the submit payload against the stored task.
 * The picker writes `recurrence` the moment it changes; it is compared anyway because a rule
 * recognised in the title (#234) is only written on close — as is a `target` (`#Projekt`).
 */
export function isDirty(task: TaskEntryData, data: TaskCreationData): boolean {
  return data.title !== task.title
    || data.target != null
    || (data.newTagNames?.length ?? 0) > 0
    || JSON.stringify(data.recurrence ?? null) !== JSON.stringify(task.recurrence ?? null)
    || data.description !== (task.description ?? null)
    || (data.attention ?? null) !== (task.attention ?? null)
    || (data.estimatedDuration ?? null) !== (task.estimatedDuration ?? null)
    || (data.durationMode ?? null) !== (task.durationMode ?? null)
    || JSON.stringify(data.tags ?? []) !== JSON.stringify(task.tags ?? [])
    || (data.rewardId ?? null) !== (task.rewardId ?? null)
    || data.startDate?.getTime() !== task.startDate?.getTime()
    || (data.startTime ?? null) !== (task.startTime ?? null)
    || (data.startPeriod ?? null) !== (task.startPeriod ?? null)
    || data.dueDate?.getTime() !== task.dueDate?.getTime()
    || (data.dueTime ?? null) !== (task.dueTime ?? null)
    || (data.duePeriod ?? null) !== (task.duePeriod ?? null)
}

interface UseTaskEntryFormArgs {
  mode: TaskEntryMode
  task?: TaskEntryData
  resetKey?: number
  selected?: boolean
  onClick?: (taskId: string, event: React.MouseEvent) => void
  onCancel?: () => void
  onSubmitCreate?: (data: TaskCreationData) => void
  onSubmitEdit?: (taskId: string, data: TaskCreationData) => void
  onSubmitCreateAndContinue?: (data: TaskCreationData) => void
  onSubmitCreateAndReveal?: (data: TaskCreationData) => void
  onSubmitEmpty?: () => void
  onSubmitEditAndCreateBelow?: (taskId: string, data: TaskCreationData) => void
  /** Checklist buffered while creating — see useSubtaskEditor. */
  subtaskDrafts: { title: string; done: boolean }[]
  /** Optional optimistic-update sink. When provided, `saveCollapsedToDb`
   *  routes badge updates through this callback instead of writing
   *  directly to Dexie. The host page (e.g. UpcomingPage) uses it to
   *  apply an optimistic UI update so the badge change is visible in the
   *  next paint instead of waiting on the IDB → LiveQuery → re-render
   *  roundtrip (~1–2s). Falls back to direct DB write when absent. */
  onContextMenuBadgeUpdate?: (taskId: string, updates: Partial<Omit<Task, 'id'>>) => void
  /**
   * Was die Erkennung im Titel (#234) aus der Eingabe macht — gesäuberter Titel, Ziel. Läuft VOR
   * `isDirty` und dem optimistischen Titel; als Ref, weil die Erkennung erst nach dem Formular steht.
   */
  transformRef?: React.RefObject<((data: TaskCreationData) => TaskCreationData) | null>
}

export function useTaskEntryForm({
  mode, task, resetKey, selected, onClick,
  onCancel, onSubmitCreate, onSubmitEdit,
  onSubmitCreateAndContinue, onSubmitCreateAndReveal, onSubmitEmpty, onSubmitEditAndCreateBelow,
  onContextMenuBadgeUpdate, subtaskDrafts, transformRef,
}: UseTaskEntryFormArgs) {
  const isExpanded = mode === 'expanded' || mode === 'creating'
  const playSoftRemove = useSoftRemoveSound()
  const playSoftAdd = useSoftAddSound()
  const source = useDataSource()

  // ── Form state ──
  const [title, setTitle] = useState(() => mode === 'expanded' && task ? task.title : '')
  const [notes, setNotes] = useState(() => mode === 'expanded' && task ? (task.description ?? '') : '')
  const [checked, setChecked] = useState(() => mode === 'expanded' && task ? task.completed : false)
  const [attention, setAttention] = useState<AttentionLevel | null>(() => mode === 'expanded' && task ? (task.attention ?? null) : null)
  const [duration, setDuration] = useState<DurationValue | null>(() => {
    if (mode === 'expanded' && task?.estimatedDuration) {
      return { minutes: task.estimatedDuration, mode: task.durationMode ?? 'duration' }
    }
    return null
  })
  const [tags, setTags] = useState<string[]>(() => mode === 'expanded' && task ? (task.tags ?? []) : [])
  const [reward, setReward] = useState<string | null>(() => mode === 'expanded' && task ? (task.rewardId ?? null) : null)
  const [start, setStart] = useState<StartValue | null>(() => {
    if (mode === 'expanded' && task?.startDate) {
      return { date: task.startDate, time: task.startTime ?? null, period: task.startPeriod ?? null }
    }
    return null
  })
  const [recurrence, setRecurrence] = useState<RecurrenceRule | null>(() => mode === 'expanded' && task ? (task.recurrence ?? null) : null)
  const [deadline, setDeadline] = useState<DeadlineValue | null>(() => {
    if (mode === 'expanded' && task?.dueDate) {
      return { date: task.dueDate, time: task.dueTime ?? null, period: task.duePeriod ?? null }
    }
    return null
  })

  // ── DB persistence ──
  // If the host wired an optimistic-update sink, route through it. The
  // sink is responsible for both showing the change instantly AND
  // persisting it to Dexie — skipping our local write avoids a double
  // round-trip. Falls back to a write through the data source when absent.
  const saveCollapsedToDb = useCallback(async (updates: Partial<Omit<Task, 'id'>>) => {
    if (!task) return
    if (onContextMenuBadgeUpdate) {
      onContextMenuBadgeUpdate(task.id, updates)
      return
    }
    try {
      await source.updateTask(task.id, updates)
    } catch (err) {
      console.error('[TaskEntry] Failed to save task update:', err)
    }
  }, [task, onContextMenuBadgeUpdate, source])

  // Same persistence, but only while editing (the expanded badge handlers
  // must not write for a row that is merely collapsed-rendered).
  const saveToDb = useCallback(async (updates: Partial<Omit<Task, 'id'>>) => {
    if (mode !== 'expanded') return
    await saveCollapsedToDb(updates)
  }, [mode, saveCollapsedToDb])

  const selectOnDropdownOpen = useCallback((open: boolean) => {
    if (open && !selected && task) {
      // Minimal stub — handleSelect only reads metaKey/shiftKey/ctrlKey for multi-select
      onClick?.(task.id, { metaKey: false, shiftKey: false, ctrlKey: false } as React.MouseEvent)
    }
  }, [selected, task, onClick])

  // ── Change handlers ──
  const handleAttentionChange = useCallback((value: AttentionLevel | null) => {
    if (value !== null && attention === null) playSoftAdd()
    setAttention(value)
    saveToDb({ attention: value })
  }, [saveToDb, playSoftAdd, attention])

  const handleDurationChange = useCallback((value: DurationValue | null) => {
    if (value !== null && duration === null) playSoftAdd()
    setDuration(value)
    saveToDb({
      estimatedDuration: value?.minutes ?? null,
      durationMode: value?.mode ?? null,
    })
  }, [saveToDb, playSoftAdd, duration])

  const handleTagsChange = useCallback((value: string[]) => {
    if (value.length > tags.length) playSoftAdd()
    setTags(value)
    saveToDb({ tags: value })
  }, [saveToDb, tags, playSoftAdd])

  /**
   * Attach (or clear) the reward. Picking one also records it in the usage
   * history that feeds "Zuletzt benutzt" — through the data source; in the app that is Dexie, which
   * queues the sync change and schedules the actual sync run.
   *
   * Goes through `saveCollapsedToDb`, not `saveToDb`: the collapsed row's
   * reward icon opens the picker too, and that write must land even though the
   * task is not expanded. Creating mode is still safe — `saveCollapsedToDb`
   * bails without a task, so a new task carries its reward in
   * `TaskCreationData` until submit.
   */
  const handleRewardChange = useCallback((value: string | null) => {
    if (value !== null && reward === null) playSoftAdd()
    setReward(value)
    saveCollapsedToDb({ rewardId: value })
    if (value) {
      source.recordRewardUsage(value).catch(err =>
        console.error('[TaskEntry] Failed to record reward usage:', err))
    }
  }, [saveCollapsedToDb, playSoftAdd, reward, source])

  const handleStartChange = useCallback((value: StartValue | null) => {
    if (value !== null && start === null) playSoftAdd()
    setStart(value)
    saveToDb({
      startDate: value?.date ?? null,
      startTime: value?.time ?? null,
      startPeriod: value?.period ?? null,
    })
  }, [saveToDb, playSoftAdd, start])

  const handleDeadlineChange = useCallback((value: DeadlineValue | null) => {
    if (value !== null && deadline === null) playSoftAdd()
    setDeadline(value)
    saveToDb({
      dueDate: value?.date ?? null,
      dueTime: value?.time ?? null,
      duePeriod: value?.period ?? null,
    })
  }, [saveToDb, playSoftAdd, deadline])

  /**
   * **Wiederkehrend** in der geöffneten Karte (Plan recurrence-in-expanded-card). Ein fester
   * Rhythmus ohne Wann bekommt seinen ersten Termin — und der muss AUCH ins Formular-Feld
   * `start`: Stünde dort noch der leere Termin, läse `isDirty` beim Schließen „Wann entfernt"
   * und schriebe den eben gesetzten Termin wieder weg. Geöffnet wird sofort gespeichert, im
   * Anlege-Modus reist die Regel in `TaskCreationData` mit (`saveToDb` schreibt dort nichts).
   */
  const handleRecurrenceChange = useCallback((rule: RecurrenceRule | null) => {
    if (rule !== null && recurrence === null) playSoftAdd()
    setRecurrence(rule)
    const patch = recurrenceUpdate(rule, new Date())
    if (patch.startDate) setStart({ date: patch.startDate, time: null, period: null })
    saveToDb(patch)
  }, [saveToDb, playSoftAdd, recurrence])

  // ── Form state initialization on mode change ──
  // A row is usually mounted collapsed and switches to expanded in place, so
  // the useState initialisers above ran with `mode === 'collapsed'` and left
  // every field empty. EVERY field the form owns has to be re-read from the
  // task here — one that is missing shows up blank AND is written back blank
  // on close, because `isDirty` then reads it as "the user cleared it".
  const formInitModeRef = useRef(mode)
  if (formInitModeRef.current !== mode) {
    formInitModeRef.current = mode
    if (mode === 'expanded' && task) {
      setTitle(task.title)
      setNotes(task.description ?? '')
      setChecked(task.completed)
      setAttention(task.attention ?? null)
      setDuration(task.estimatedDuration ? { minutes: task.estimatedDuration, mode: task.durationMode ?? 'duration' } : null)
      setTags(task.tags ?? [])
      setReward(task.rewardId ?? null)
      setStart(task.startDate ? { date: task.startDate, time: task.startTime ?? null, period: task.startPeriod ?? null } : null)
      setDeadline(task.dueDate ? { date: task.dueDate, time: task.dueTime ?? null, period: task.duePeriod ?? null } : null)
      setRecurrence(task.recurrence ?? null)
    }
    if (mode === 'creating') {
      setTitle(''); setNotes(''); setChecked(false); setAttention(null)
      setDuration(null); setTags([]); setReward(null); setStart(null); setDeadline(null); setRecurrence(null)
    }
  }

  // Reset form when resetKey changes (Cmd+Enter continue-creation)
  const prevResetKeyRef = useRef(resetKey)
  if (prevResetKeyRef.current !== resetKey) {
    prevResetKeyRef.current = resetKey
    if (mode === 'creating') {
      setTitle(''); setNotes(''); setChecked(false); setAttention(null)
      setDuration(null); setTags([]); setReward(null); setStart(null); setDeadline(null); setRecurrence(null)
    }
  }

  // ── Optimistic title ──
  const optimisticTitleRef = useRef<string | null>(null)
  if (optimisticTitleRef.current !== null && task?.title === optimisticTitleRef.current) {
    optimisticTitleRef.current = null
  }

  // ── Submit logic ──
  const buildData = useCallback(() => {
    const data = buildCreationData({ title, notes, attention, duration, tags, reward, start, deadline, subtaskDrafts, recurrence })
    return transformRef?.current ? transformRef.current(data) : data
  }, [title, notes, attention, duration, tags, reward, start, deadline, subtaskDrafts, recurrence, transformRef])

  const handleSubmit = useCallback(() => {
    const data = buildData()
    if (!data.title) {
      if (mode === 'creating' && onSubmitEmpty) onSubmitEmpty()
      else onCancel?.()
      return
    }

    if (mode === 'creating') {
      onSubmitCreate?.(data)
    } else if (mode === 'expanded' && task) {
      if (!isDirty(task, data)) { onCancel?.(); return }
      optimisticTitleRef.current = data.title
      onSubmitEdit?.(task.id, data)
    }
  }, [mode, task, buildData, onSubmitCreate, onSubmitEdit, onCancel, onSubmitEmpty])

  const handleSubmitAndCreateNew = useCallback(() => {
    const data = buildData()
    if (!data.title) {
      if (mode === 'creating') onSubmitEmpty?.()
      return
    }

    if (mode === 'creating') {
      onSubmitCreateAndContinue?.(data)
    } else if (mode === 'expanded' && task) {
      if (isDirty(task, data)) optimisticTitleRef.current = data.title
      onSubmitEditAndCreateBelow?.(task.id, data)
    }
  }, [mode, task, buildData, onSubmitCreateAndContinue, onSubmitEditAndCreateBelow, onSubmitEmpty])

  const handleSubmitAndReveal = useCallback(() => {
    if (mode !== 'creating') return
    const data = buildData()
    if (!data.title) { onSubmitEmpty?.(); return }
    onSubmitCreateAndReveal?.(data)
  }, [mode, buildData, onSubmitCreateAndReveal, onSubmitEmpty])

  // Stable ref for pointerdown listener
  const handleSubmitRef = useRef<(() => void) | null>(null)
  handleSubmitRef.current = handleSubmit

  return {
    // Form state
    title, setTitle, notes, setNotes, checked, setChecked,
    attention, setAttention, duration, setDuration,
    tags, setTags, reward, setReward, start, setStart, deadline, setDeadline, recurrence, setRecurrence,
    // Handlers
    handleAttentionChange, handleDurationChange, handleTagsChange, handleRewardChange,
    handleStartChange, handleDeadlineChange, handleRecurrenceChange,
    saveToDb, saveCollapsedToDb, selectOnDropdownOpen,
    // Submit
    buildData, handleSubmit, handleSubmitAndCreateNew, handleSubmitAndReveal, handleSubmitRef, optimisticTitleRef,
    // Sounds
    playSoftRemove, playSoftAdd,
    // Derived
    isExpanded,
  }
}
